import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchCaseResourceAssignments,
  fetchCaseRecentActivity,
  fetchAllResources,
  fetchUserCaseGuardians,
  fetchUserCaseMembers,
  postResourceAssignment,
} from '../../../../services/httpClient';
import ToastMsg from '../../../ui/ToastMsg';
import { Button, Card, Col, Container, Row, Spinner, Stack, Table } from 'react-bootstrap';
import ResourceCard from './ResourceCard';
import SelectFamily from '../../../ui/SelectFamily';
import SelectObjectTypeahead from '../../../ui/SelectObjectTypeahead';
import SelectObject from '../../../ui/SelectObject';
import ReactPaginate from 'react-paginate';

interface ResourcesState {
  viewResources: boolean;
  setViewResources: any;
  viewRecentActivity: boolean;
  setViewRecentActivity: any;
  assignResource: boolean;
  setAssignResource: any;
  getData: boolean;
}

const itemsPerPage = 25;

const Resources = ({
  viewResources,
  setViewResources,
  viewRecentActivity,
  setViewRecentActivity,
  assignResource,
  setAssignResource,
  getData,
}: ResourcesState) => {
  const { id } = useParams();
  const [resourceAssignments, setResourceAssignments] = useState<any[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [family, setFamily] = useState<any>([]);
  const [resourceOptions, setResourceOptions] = useState<any[]>([]);
  const [selectedResource, setSelectedResource] = useState<any>(null);
  const [quantitiesAvailable, setQuantitiesAvailable] = useState<any[]>([]);
  const [assignedTo, setAssignedTo] = useState<string>("0");
  const [quantityToAssign, setQuantityToAssign] = useState<number>(1);
  const [allowAssignResource, setAllowAssignResource] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [errMsg, setErrMsg] = useState<string>("");  
  const [currentPage, setCurrentPage] = useState<number>(0);

  const hasResources = resourceAssignments.some((a: any) => a.resources.length > 0);
  const pageCount = Math.ceil(recentActivity.length / itemsPerPage);
  const offset = currentPage * itemsPerPage;
  const currentActivity = recentActivity.slice(offset, offset + itemsPerPage);

  const updateViewResources = (e: any) => {
    setErrMsg("");
    setViewResources(e);
    setViewRecentActivity(false);
    setAssignResource(false);
  }

  const updateViewRecentActivity = (e: any) => {
    setErrMsg("");
    setViewResources(false);
    setViewRecentActivity(e);
    setAssignResource(false);
  }

  useEffect(() => {
    if (getData) {
      setIsLoading(true);
      getResourceAssignments();
      getRecentActivity();
      getFamily();
      getResources();
    }
  }, [getData]);

  useEffect(() => {
    const validInput = selectedResource !== null && assignedTo !== "0";
    validInput ? setAllowAssignResource(true) : setAllowAssignResource(false);
  }, [selectedResource, assignedTo]);

  const getResourceAssignments = () => {
    fetchCaseResourceAssignments(id)
      .then((res: any) => {
        const assignments = res[0].data.map((a: any) => ({
          ...a,
          resources: a.resources.filter((r: any) => r.resource_type_key === "nonconsumable"),
        }));
        setResourceAssignments(assignments);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getRecentActivity = () => {
    fetchCaseRecentActivity(id)
      .then((res: any) => {
        const activity = res[0].data;
        setRecentActivity(activity);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getFamily = () => {
    fetchUserCaseGuardians(id, true).then((res: any) => {
      const guardians = res[0].data.map((g: any) => ({
        id: g.id,
        type: "guardian",
        key: g.firstname + " " + g.lastname,
        name: g.firstname + " " + g.lastname,
        isDeleted: g.deleted_at ? true : false,
      }));

      fetchUserCaseMembers(id, true).then((res: any) => {
        const members = res[0].data.map((m: any) => ({
          id: m.id,
          type: "member",
          key: m.firstname + " " + m.lastname,
          name: m.firstname + " " + m.lastname,
          isDeleted: m.deleted_at ? true : false,
        }));

        const family = [...guardians, ...members];
        family.sort((a: any, b: any) => a.isDeleted - b.isDeleted);
        setFamily(family.filter((i: any) => i.isDeleted !== true));
        setIsLoading(false);
      });
    })
    .catch((err: any) => {
      console.log(err);
      setIsLoading(false);
    });
  };

  const getResources = () => {
    fetchAllResources()
      .then((res: any) => {
        const resources = res[0].data.filter((r: any) => !r.deleted_at);

        const consumables = resources
          .filter((r: any) => r.type_key === "consumable" && r.quantity > 0)
          .map((r: any) => ({
            value: r.id,
            label: r.name,
            original: r,
          }));

        const nonConsumables = resources
          .filter((r: any) => r.type_key === "nonconsumable")
          .map((r: any) => ({
            value: r.id,
            label: r.name,
            original: r,
            isDisabled: r.remaining === 0,
          }))
          .sort((a: any, b: any) => {
            return Number(a.isDisabled) - Number(b.isDisabled);
          });

        const groupedResources = [
          { label: "Consumable", options: consumables },
          { label: "Non-Consumable", options: nonConsumables },
        ];

        setResourceOptions(groupedResources);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const formatResourceName = (name: string) => {
    if (name.endsWith("s")) {
      return name.replace(/s$/, "(s)");
    }
    return `${name}(s)`;
  };

  const onClickSaveResourceAssignment = () => {
    setErrMsg("");

    const newAssignment: any = {
      case_id: Number(id),
      resource_id: Number(selectedResource.original.id),
      quantity: Number(quantityToAssign),
    };

    const [idValue, typeValue] = assignedTo.split("|");

    typeValue === "guardian"
      ? newAssignment.user_id = Number(idValue)
      : newAssignment.member_id = Number(idValue);

    postResourceAssignment(newAssignment)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Resource Assigned");
        setToastBody(`${quantityToAssign} ${formatResourceName(selectedResource.original.name)}`);
        setSelectedResource(null);
        setQuantityToAssign(1);
        setQuantitiesAvailable([]);
        setAssignResource(false);
        if (family.length === 1) {
          setAssignedTo(`${family[0].id}|${family[0].type}`);
        } else {
          setAssignedTo("0");
        }
        getResourceAssignments();
        getRecentActivity();
        getResources();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickAssignResource = () => {
    setErrMsg("");
    setAssignResource(true);
    setSelectedResource(null);
    setQuantityToAssign(1);
    setQuantitiesAvailable([]);
    
    if (family.length === 1) {
      setAssignedTo(`${family[0].id}|${family[0].type}`);
    } else {
      setAssignedTo("0");
    }
  };

  const onClickAssignResourceCancel = () => {
    setErrMsg("");
    setAssignResource(false);
  };

  const updateSelectedResource = (e: any) => {
    const formattedResource = {
      value: e.id,
      label: e.name,
      original: e,
    };
    
    const available = e.type_key === "consumable" ? e.quantity : e.remaining;
    const quantityOptions = Array.from({ length: available }, (_, i) => ({
      key: i + 1,
      name: i + 1,
    }));

    setSelectedResource(formattedResource);
    setQuantitiesAvailable(quantityOptions);
  };

  const formatActivityDate = (dateTime: string) => {
    const utcTimeString = dateTime;
    const utcDate = new Date(utcTimeString);
    const utcOffsetMinutes = utcDate.getTimezoneOffset();
    const utcOffsetHours = utcOffsetMinutes / 60;

    const localTime = new Date(utcDate.getTime() - utcOffsetHours * 60 * 60 * 1000);

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const weekday = weekdayNames[localTime.getDay()];
    const month = monthNames[localTime.getMonth()];
    const day = localTime.getDate();

    const formattedDate = `${weekday}, ${month} ${day}`;

    return formattedDate;
  };

  const formatActivityTime = (dateTime: string) => {
    const utcTimeString = dateTime;
    const utcDate = new Date(utcTimeString);
    const utcOffsetMinutes = utcDate.getTimezoneOffset();
    const utcOffsetHours = utcOffsetMinutes / 60;

    const localTime = new Date(utcDate.getTime() - utcOffsetHours * 60 * 60 * 1000);

    const hour = localTime.getHours();
    const minute = localTime.getMinutes();
    const isAM = hour < 12;
    const hour12 = isAM ? hour : hour === 12 ? 12 : hour - 12;
    const period = isAM ? "am" : "pm";

    const formattedDate = `${hour12}:${minute.toString().padStart(2, "0")} ${period}`;

    return formattedDate;
  };

  const handlePageClick = (event: { selected: number }) => {
    setCurrentPage(event.selected);
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          {viewResources && (
            <>
              <Row className="mb-3">
                <Col xs={1} lg={3}>
                  <h4 className="app-header-text">Resources</h4>
                </Col>
                <Col xs={11} lg={9} className="text-end">
                  {!assignResource && (
                    <Button
                      className="app-secondary-color me-3 button"
                      onClick={() => updateViewRecentActivity(true)}
                    >
                      Recent Activity
                    </Button>
                  )}
                  <Button
                    className="app-primary-color button"
                    onClick={assignResource ? onClickAssignResourceCancel : onClickAssignResource}
                  >
                    {assignResource ? "Cancel" : "Assign Resource"}
                  </Button>
                </Col>
              </Row>
              {assignResource && (
                <Card className="bg-light mb-3">
                  <Card.Body>
                    <Stack direction="horizontal" gap={3}>
                      <div style={{ width: "40%" }}>
                        <SelectObjectTypeahead
                          label="Resource"
                          value={selectedResource}
                          options={resourceOptions}
                          onUpdate={(e: any) => updateSelectedResource(e)}
                          focused
                          groupHeading
                        />
                      </div>
                      <div style={{ width: "40%" }}>
                        <SelectFamily
                          skipDefaultSelection={family.length === 1}
                          family={family}
                          familyUser={assignedTo}
                          updateFamilyUser={(e: any) => setAssignedTo(e.target.value)}
                          fullSize
                        />
                      </div>
                      <div style={{ width: "15%" }}>
                        <SelectObject
                          initialOption="Select"
                          label="Quantity"
                          value={quantityToAssign}
                          options={quantitiesAvailable}
                          onUpdate={(e: any) => setQuantityToAssign(e.target.value)}
                        />
                      </div>
                      <Button
                        className="app-secondary-color button"
                        onClick={onClickSaveResourceAssignment}
                        disabled={!allowAssignResource}
                      >
                        Assign
                      </Button>
                    </Stack>
                  </Card.Body>
                </Card>
              )}
              <Row>
                <Col xs={12}>
                  {!hasResources ? (
                    <p className="mt-2">No Resources Currently Assigned</p>
                  ) : (
                    resourceAssignments.map((assignment: any, index: number) => (
                      <div key={index} className="mb-4">
                        {family.length > 1 && assignment.resources.length > 0 && (
                          <h5 className="app-header-text mb-2">
                            {assignment.assignee_name}
                          </h5>
                        )}
                        {assignment.resources.map((resource: any, index: number) => (
                          <ResourceCard
                            key={index}
                            resource={resource}
                            assignedQuantity={resource.quantity}
                            assigneeId={assignment.assignee_id}
                            assigneeType={assignment.assignee_type}
                            getResourceAssignments={getResourceAssignments}
                            getRecentActivity={getRecentActivity}
                            formatResourceName={formatResourceName}
                            setShowToast={setShowToast}
                            setToastText={setToastText}
                            setToastBody={setToastBody}
                            setErrMsg={setErrMsg}
                          />
                        ))}
                      </div>
                    ))
                  )}
                </Col>
              </Row>
            </>
          )}
          {viewRecentActivity && (
            <>
              <Row className="mb-0">
                <Col>
                  <h4 className="app-header-text">Recent Activity</h4>
                </Col>
                <Col className="text-end">
                  <Button
                    className="app-primary-color button"
                    onClick={() => updateViewResources(true)}
                  >
                    Back
                  </Button>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col xs={12}>
                  {currentActivity.length === 0 ? (
                    <p className="mt-2">No Recent Activity</p>
                  ) : (
                    <>
                      <Table striped>
                        <thead>
                          <tr>
                            <th>Resource</th>
                            <th>Quantity</th>
                            <th>Person</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentActivity.map((a: any, index: number) => (
                            <tr key={index}>
                              <td>{a.resource_name}</td>
                              <td>{a.quantity}</td>
                              <td>{a.assignee_name}</td>
                              <td>
                                {a.activity_type === "assign" ? "Assigned on " : "Returned on "}
                                {`${formatActivityDate(a.activity_date)} ${formatActivityTime(a.activity_date)}`}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <div id="programs-pagination">
                        <ReactPaginate
                          previousLabel={"← Previous"}
                          nextLabel={"Next →"}
                          pageCount={pageCount}
                          onPageChange={handlePageClick}
                          containerClassName={"pagination justify-content-center"}
                          pageClassName={"page-item"}
                          pageLinkClassName={"page-link"}
                          previousClassName={"page-item"}
                          previousLinkClassName={"page-link"}
                          nextClassName={"page-item"}
                          nextLinkClassName={"page-link"}
                          activeClassName={"active"}
                          forcePage={currentPage}
                        />
                      </div>
                    </>
                  )}
                </Col>
              </Row>
            </>
          )}
        </>
      )}
    </>
  );
};

export default Resources;
